/**
 * Разбор матрицы ошибок в сетку 3×3 и производные показатели.
 *
 * Тут нет ни генерации, ни подстановки значений: пустой набор ячеек остаётся
 * пустым, и панель показывает «нет размеченных транзакций». Ручка BLOCK 3 на
 * пустых таблицах отдаёт 200 и пустой `cells`, и это сознательно — рисовать
 * вместо данных сетку нулей значит показать аналитику «ноль ошибок» там, где
 * на самом деле «нет замеров».
 */

import type { ConfusionCell, ConfusionMatrix } from "./api"

/** Порядок строк и колонок — как в образце SEON: approve → review → decline. */
export const OUTCOMES = ["approve", "review", "decline"] as const
export type MatrixOutcome = (typeof OUTCOMES)[number]

export function toNumber(value: string | number | null | undefined) {
  if (value === null || value === undefined || value === "") return 0
  const numeric = typeof value === "number" ? value : Number(value)
  return Number.isNaN(numeric) ? 0 : numeric
}

export function hasMatrixData(matrix: ConfusionMatrix | null | undefined) {
  return Boolean(matrix && matrix.cells.length > 0)
}

/**
 * Сетка `[actual][predicted]`. Отсутствующая ячейка остаётся `null` — так в
 * таблице видно разницу между «пришёл ноль» и «ячейки не было в ответе».
 */
export function matrixGrid(matrix: ConfusionMatrix) {
  const index = new Map<string, ConfusionCell>()
  for (const cell of matrix.cells) {
    index.set(`${cell.actual}|${cell.predicted}`, cell)
  }
  return OUTCOMES.map((actual) =>
    OUTCOMES.map((predicted) => index.get(`${actual}|${predicted}`) ?? null),
  )
}

export interface MatrixMetrics {
  /** Доля верных решений: диагональ / все ячейки. */
  accuracy: number | null
  /** Точность по `decline`: TP / (всё, что система отклонила). */
  precision: number | null
  /** Полнота по `decline`: TP / (всё, что было мошенничеством). */
  recall: number | null
}

/**
 * Показатели считаются по всем девяти ячейкам, а `decline` берётся за
 * положительный класс — именно его ловит скоринг.
 *
 * Метки TP/TN/FP/FN в образце стоят только на четырёх угловых ячейках
 * (approve/decline), потому что бинарная терминология на трёх классах
 * неоднозначна. Мы эту разметку повторяем как есть и не додумываем метки для
 * строки и колонки `review`; при этом сами показатели считаются по полной
 * сетке, иначе `review`-трафик молча выпал бы из знаменателя.
 *
 * Знаменатель нулевой → `null`, а не `0%`: «данных нет» и «ноль процентов» для
 * аналитика это разные утверждения.
 */
export function matrixMetrics(matrix: ConfusionMatrix): MatrixMetrics {
  if (!hasMatrixData(matrix)) {
    return { accuracy: null, precision: null, recall: null }
  }

  const grid = matrixGrid(matrix)
  const count = (actual: number, predicted: number) =>
    toNumber(grid[actual][predicted]?.count)

  let total = 0
  let diagonal = 0
  for (let row = 0; row < OUTCOMES.length; row += 1) {
    for (let col = 0; col < OUTCOMES.length; col += 1) {
      const value = count(row, col)
      total += value
      if (row === col) diagonal += value
    }
  }

  const decline = OUTCOMES.indexOf("decline")
  const truePositive = count(decline, decline)
  let predictedDecline = 0
  let actualDecline = 0
  for (let i = 0; i < OUTCOMES.length; i += 1) {
    predictedDecline += count(i, decline)
    actualDecline += count(decline, i)
  }

  const ratio = (numerator: number, denominator: number) =>
    denominator > 0 ? (numerator / denominator) * 100 : null

  return {
    accuracy: ratio(diagonal, total),
    precision: ratio(truePositive, predictedDecline),
    recall: ratio(truePositive, actualDecline),
  }
}
